import {
  CreateJsonDataContainerPayload,
  UpdateJsonDataContainerPayload,
  useAddJsonDataBlobComponentMutation,
  useCreateJsonDataContainerMutation,
  useEventIDByHandleQuery,
  useUpdateJsonDataContainerMutation,
} from '@graphql/generated';
import { useCallback } from 'react';

export const useSaveJsonData = () => {
  const [callSaveJsonData] = useUpdateJsonDataContainerMutation();

  const saveJsonData = useCallback(
    async (payload: UpdateJsonDataContainerPayload) => {
      try {
        const { data, errors } = await callSaveJsonData({
          variables: { payload },
        });

        if (!data) throw new Error('No data returned');

        return { data: data.updateJsonDataContainer, errors };
      } catch (error) {
        return { data: null, errors: error };
      }
    },
    [callSaveJsonData],
  );

  return { saveJsonData };
};

export const useCreateJsonDataContainer = () => {
  const [callCreateJsonData] = useCreateJsonDataContainerMutation();

  const createJsonDataContainer = useCallback(
    async (payload: CreateJsonDataContainerPayload) => {
      try {
        const { data, errors } = await callCreateJsonData({
          variables: { payload },
        });

        if (!data) throw new Error('No data returned');

        return { data: data.createJsonDataContainer, errors: errors };
      } catch (error) {
        return { data: null, errors: error };
      }
    },
    [callCreateJsonData],
  );

  return { createJsonDataContainer };
};

type CreateJsonComponentPayload = {
  containerId: string;
  schemaURL: string;
  type: string;
  initialData: object;
};

export const useCreateNewJsonComponent = (eventHandle: string) => {
  const { createJsonDataContainer } = useCreateJsonDataContainer();
  const [callCreateComponent] = useAddJsonDataBlobComponentMutation();
  const { data: eventData, loading: eventDataLoading } = useEventIDByHandleQuery({
    variables: {
      name: eventHandle,
    },
    batchMode: 'fast',
  });

  if (!eventDataLoading && !eventData?.eventByName?.id) {
    throw new Error('Event not found');
  }

  const createNewJsonComponent = useCallback(
    async (payload: CreateJsonComponentPayload) => {
      const eventId = eventData?.eventByName?.id;
      if (!eventId) {
        throw new Error('Event not found');
      }

      const createContainerData = await createJsonDataContainer({
        content: payload.initialData,
        eventId,
        schemaUrl: payload.schemaURL,
        type: payload.type,
      });

      if (!createContainerData.data) {
        return { data: null, error: createContainerData.errors };
      }

      const { data: createComponentData, errors: createComponentErrors } = await callCreateComponent({
        variables: {
          payload: {
            containerId: payload.containerId,
            jsonDataContainerId: createContainerData.data.id,
            sortOrder: 0,
          },
        },
      });

      if (!createComponentData) {
        throw new Error('No data returned from createComponent');
      }

      if (createComponentErrors) {
        return {
          componentData: null,
          containerData: null,
          errors: createComponentErrors,
        };
      }
      return {
        componentData: createComponentData.addJsonDataBlobComponent,
        containerData: createContainerData,
        errors: null,
      };
    },
    [eventData, callCreateComponent, createJsonDataContainer],
  );

  return { createNewJsonComponent };
};

/**
 * Returns useful queries and mutations for JsonDataContainers
 */
export const useJsonContainerTools = (eventHandle: string) => {
  const { saveJsonData } = useSaveJsonData();
  const { createJsonDataContainer } = useCreateJsonDataContainer();
  const { createNewJsonComponent } = useCreateNewJsonComponent(eventHandle);

  return {
    saveJsonData,
    createJsonDataContainer,
    createNewJsonComponent,
  };
};
